// HUD controller. Builds the DOM, subscribes to events, exposes mountPaw().
// Keeps all DOM string templates in one place so styling stays close to markup.

import { clicksPerRow } from '../game/riceGrowth.js'
import gsap from 'gsap'
import { Events, on, emit } from './eventBus.js'
import { getWorkMode, toggleWorkMode, getClickValue, getSeasonConfig } from '../game/seasonManager.js'
import { WorkModes, ClickerNudgeMs, RiceGrowth, PlotTypes, LoopFeedback, GlintConfig } from '../config/balance.js'
import { countByType } from '../game/plotState.js'
import { formatCoins, formatCoinsDelta, formatUsd, coinsToUsd } from './format.js'
import { currentAdventure as advCurrent, currentDef as advCurrentDef, recall as advRecall } from '../game/adventureManager.js'
import * as idle from '../game/idleManager.js'

export function mountHud({ root }) {
  root.innerHTML = template()

  const els = {
    wealth: root.querySelector('[data-wealth]'),
    wealthUsd: root.querySelector('[data-wealth-usd]'),
    seasonName: root.querySelector('[data-season-name]'),
    seasonCount: root.querySelector('[data-season-count]'),
    seasonBar: root.querySelector('[data-season-bar]'),
    pawYield: root.querySelector('[data-paw-yield]'),
    log: root.querySelector('[data-log]'),
    paw: root.querySelector('[data-paw]'),
    streak: root.querySelector('[data-streak]'),
    streakNum: root.querySelector('[data-streak-num]'),
    holiday: root.querySelector('[data-holiday]'),
    holidayEmoji: root.querySelector('[data-holiday-emoji]'),
    holidayLabel: root.querySelector('[data-holiday-label]'),
    holidayMult: root.querySelector('[data-holiday-mult]'),
    advStatus: root.querySelector('[data-adv-status]'),
    advText:   root.querySelector('[data-adv-text]'),
    advMode:   root.querySelector('[data-adv-mode]'),
  }

  // Adventure status chip — visible only while the cat is on an adventure.
  // Shows biome + current mode; updates on start/end/mode-change.
  const paintAdventure = () => {
    if (!els.advStatus) return
    const a = advCurrent()
    const def = advCurrentDef()
    if (a && def) {
      els.advText.textContent = def.name
      els.advMode.textContent = a.mode === 'DEFENSE' ? '🛡 DEFENSE'
        : a.mode === 'DISCOVERY' ? '🔎 DISCOVERY'
        : '• IDLE' // NEUTRAL — reticle off, no stance bonus
      els.advStatus.dataset.mode = a.mode
      els.advStatus.hidden = false
    } else {
      els.advStatus.hidden = true
    }
  }
  on(Events.AdventureStart,      paintAdventure)
  on(Events.AdventureEnd,        paintAdventure)
  on(Events.AdventureModeChange, paintAdventure)
  // Obvious "Return Home" on the adventure chip — recall the cat (keep 70% of
  // coins found). Confirms first so a stray tap doesn't end the run.
  root.querySelector('[data-adv-return]')?.addEventListener('click', () => {
    if (window.confirm('Bring your cat home now? You keep 70% of the coins discovered so far.')) advRecall()
  })
  // Repaint on boot in case adventure was already underway from a prior session.
  paintAdventure()

  // Holiday banner — visible whenever a festival window is active. Multi-
  // plier badge shows the click bonus so players know the bonus is on.
  const paintHoliday = ({ holiday }) => {
    if (!els.holiday) return
    if (holiday) {
      els.holidayEmoji.textContent = holiday.emoji ?? '🎉'
      els.holidayLabel.textContent = holiday.label
      els.holidayMult.textContent = `+${Math.round((holiday.clickMultiplier - 1) * 100)}% clicks`
      els.holiday.hidden = false
      els.holiday.classList.remove('pulse')
      void els.holiday.offsetWidth
      els.holiday.classList.add('pulse')
    } else {
      els.holiday.hidden = true
    }
  }
  on(Events.HolidayChange, paintHoliday)

  // Streak chip — hidden when 0 (anonymous account or pre-account-ready),
  // visible at 1+. Pulses gently on each new-day bump.
  const paintStreak = ({ current }) => {
    if (!els.streak) return
    if (current > 0) {
      els.streak.hidden = false
      els.streakNum.textContent = String(current)
      // CSS animation runs once per re-add of the .pulse class.
      els.streak.classList.remove('pulse')
      void els.streak.offsetWidth // force reflow so the class re-toggle restarts the keyframes
      els.streak.classList.add('pulse')
    } else {
      els.streak.hidden = true
    }
  }
  on(Events.StreakChange, paintStreak)

  const refreshPawYield = () => {
    // CR-2: per-click value is the pure formula (5 +1/PhD +2/peasant +4/scribe,
    // ×2 in fields). Shows the player exactly what each tap is worth right now.
    els.pawYield.textContent = `${formatCoinsDelta(getClickValue())} / click`
    els.pawYield.classList.remove('zero')
  }
  on(Events.SeasonChange, refreshPawYield)
  on(Events.GoonOwnedChange, refreshPawYield) // buying/losing citizens changes click value
  on(Events.WorkModeChange, refreshPawYield)  // fields ×2 toggle
  on(Events.CitizenInjured, refreshPawYield)
  on(Events.CitizenHealed, refreshPawYield)
  on(Events.KingdomChange, refreshPawYield)

  // Work Mode toggle — Fields (max income, exposed) ⇄ Village (defended, less income).
  const wmBtn = root.querySelector('[data-workmode]')
  const wmIcon = root.querySelector('[data-wm-icon]')
  const wmTitle = root.querySelector('[data-wm-title]')
  const wmAction = root.querySelector('[data-wm-action]')
  const wmSub = root.querySelector('[data-wm-sub]')
  const refreshWorkMode = () => {
    const fields = getWorkMode() === WorkModes.FIELDS
    wmBtn.classList.toggle('village', !fields)
    wmIcon.textContent = fields ? '⚒' : '🛡'
    // Three lines, no ambiguity: current state · what a click does · the trade-off.
    wmTitle.textContent = fields ? 'WORKERS ARE IN THE FIELDS' : 'WORKERS ARE IN THE VILLAGE'
    wmAction.textContent = fields
      ? 'Click to recall them to defend the village'
      : 'Click to send them out to farm the fields'
    wmSub.textContent = fields
      ? 'Max rice income · village undefended'
      : 'Village defended · rice income reduced'
  }
  wmBtn.addEventListener('click', () => toggleWorkMode())
  on(Events.WorkModeChange, ({ mode }) => {
    refreshWorkMode()
    refreshPawYield()
    pushLog(mode === WorkModes.FIELDS
      ? '⚒ Workers sent to the fields — rice income is flowing, but the village is undefended. If goons attack, click the work-mode button to recall them.'
      : '🛡 Workers recalled to guard the village — buildings are safe, but rice income drops. Click the work-mode button to send them back to the fields.')
  })
  on(Events.KingdomChange, refreshWorkMode)
  refreshWorkMode()

  const wealthDisplay = { value: 0 }
  const paintWealth = () => {
    const v = Math.floor(wealthDisplay.value)
    els.wealth.textContent = formatCoins(v)
    if (els.wealthUsd) els.wealthUsd.textContent = `≈ ${formatUsd(coinsToUsd(v))}`
  }
  let latestWealth = 0
  let tallying = false
  // A purchase tally: the treasury counts up slowly, glows, and a "+N" floats up from it.
  const tallyTo = (wealth, delta) => {
    tallying = true
    gsap.killTweensOf(wealthDisplay)
    els.wealth.classList.add('tallying')
    const box = els.wealth.parentElement
    if (box && delta > 0) {
      if (getComputedStyle(box).position === 'static') box.style.position = 'relative'
      const gain = document.createElement('div')
      gain.className = 'wealth-gain'
      gain.textContent = `+${formatCoins(delta)}`
      box.appendChild(gain)
      gsap.fromTo(gain, { y: 6, opacity: 0 }, { y: -26, opacity: 1, duration: 0.5, ease: 'power2.out' })
      gsap.to(gain, { y: -64, opacity: 0, duration: 1.6, delay: 1.3, ease: 'power1.in', onComplete: () => gain.remove() })
    }
    gsap.to(wealthDisplay, {
      value: wealth,
      duration: 2.6,
      ease: 'power2.out',
      onUpdate: paintWealth,
      onComplete: () => {
        tallying = false
        els.wealth.classList.remove('tallying')
        if (latestWealth !== wealth) gsap.to(wealthDisplay, { value: latestWealth, duration: 0.4, ease: 'power1.out', onUpdate: paintWealth })
      },
    })
  }
  on(Events.WealthChange, ({ wealth, delta, tally }) => {
    latestWealth = wealth
    if (tally) return tallyTo(wealth, delta)
    if (tallying) return // let the count-up finish; it settles on the latest value when it ends
    gsap.to(wealthDisplay, { value: wealth, duration: 0.4, ease: 'power1.out', onUpdate: paintWealth })
  })

  // Layer 1: always-visible season affordance — emoji glyph + per-season bar
  // color (brown→tan→green→gold) + a CSS shimmer (added below) so the player
  // sees the fields are being tended even when the paddies are off-camera.
  const paintSeasonHeader = (config) => {
    if (!config) return
    els.seasonName.textContent = `${config.emoji ? config.emoji + ' ' : ''}${config.label}`
    if (config.tileColor != null) {
      els.seasonBar.style.background = '#' + config.tileColor.toString(16).padStart(6, '0')
    }
  }
  paintSeasonHeader(getSeasonConfig()) // initial paint on boot

  // "37 / 125 rice": the season's clicks against the target that fills every rice row (RiceGrowth.clicksPerPaddy × paddies) —
  // the same count that grows the rows in the field.
  // "37 / 75 rice · 3 to next row" — and past a full field "75 / 75 rice · +4 surplus" (the extra clicks still count toward the harvest)
  const riceCount = (clicks) => {
    const t = countByType(PlotTypes.FARM) * RiceGrowth.clicksPerPaddy, per = clicksPerRow(RiceGrowth)
    const extra = !t ? '' : clicks >= t ? ` · +${clicks - t} surplus` : ` · ${per - (clicks % per)} to next row`
    els.seasonCount.textContent = t ? `${Math.min(clicks, t)} / ${t} rice${extra}` : `${clicks} rice`
    els.seasonCount.classList.toggle('full', t > 0 && clicks >= t)
  }
  on(Events.PlotChanged, () => riceCount(parseInt(els.seasonCount.textContent, 10) || 0))
  on(Events.SeasonChange, ({ config, previous }) => {
    paintSeasonHeader(config)
    riceCount(0)
    els.seasonBar.style.width = '0%'
    if (previous) { els.seasonName.classList.remove('turn'); void els.seasonName.offsetWidth; els.seasonName.classList.add('turn') } // the turn is a moment
    if (previous) pushLog(`${config.emoji ? config.emoji + ' ' : ''}${config.tip || `Season: ${config.name}`}`)
  })

  // CR-2: the season bar is now a TIME window (goons distract you → fewer clicks
  // land in it → smaller harvest). The count shows rice tended this season.
  on(Events.SeasonProgress, ({ elapsed, duration, clicks }) => {
    els.seasonBar.style.width = `${Math.min(100, Math.round((elapsed / duration) * 100))}%`
    riceCount(clicks)
  })

  on(Events.Click, ({ delta, clicks, hot }) => {
    if (typeof clicks === 'number') riceCount(clicks)
    if (delta > 0) spawnFloatingYen(els.paw, delta, els.wealth, hot ? 'hot' : '')
  })
  on(Events.CitizenTended, ({ clicks }) => { if (typeof clicks === 'number') riceCount(clicks) })
  on(Events.ClicksLost, ({ clicks }) => { if (typeof clicks === 'number') riceCount(clicks) })
  on(Events.CoinPopAt, ({ x, y, delta }) => { if (delta > 0) floatFrom(x, y, delta, els.wealth, 'coin') }) // a directed citizen's coin
  // the rhythm combo meter: fills a step a click, glows gold at the top, drains when the rhythm breaks
  const comboEl = root.querySelector('[data-combo]'), comboBar = root.querySelector('[data-combo-bar]'), comboLabel = root.querySelector('[data-combo-label]')
  on(Events.ComboChange, ({ value, mult, live, max, broke, hot = 0 }) => {
    comboBar.style.width = `${Math.round(value * 100)}%`
    comboLabel.textContent = hot > 0 ? `HOT ×${(mult * GlintConfig.boostMult).toFixed(1).replace(/\.0$/, '')} · ${hot}` : `×${mult.toFixed(mult >= 1.45 ? 1 : 2).replace(/0$/, '')}`
    comboEl.classList.toggle('hot', hot > 0)
    comboEl.classList.toggle('live', !!live && value > 0)
    comboEl.classList.toggle('max', value >= 0.999)
    if (max) { comboEl.classList.remove('pop'); void comboEl.offsetWidth; comboEl.classList.add('pop') }
    if (broke) { comboEl.classList.remove('broke'); void comboEl.offsetWidth; comboEl.classList.add('broke') }
  })

  // CR-2: end-of-HARVEST payout = (season bottleneck) × rice value.
  on(Events.Harvest, ({ harvestable, bonus }) => {
    pushLog(harvestable > 0
      ? `🌾 Harvest! ${harvestable} rice secured across all four seasons → +${formatCoins(bonus)}.`
      : '🌾 Harvest failed — one of the four seasons ended with no clicks. Your harvest equals your LOWEST season, so click in every season (prep, plant, maintain, harvest).')
  })

  // Gentle nudge: after ClickerNudgeMs without a clicker click, the ring pulses green → white until the next click.
  let lastPawAt = Date.now()
  const updateNudge = () => {
    const idle = Date.now() - lastPawAt >= ClickerNudgeMs
    const show = idle && document.body.dataset.activePage === 'play' && !document.body.classList.contains('in-capital') && !document.body.classList.contains('in-land') && !root.classList.contains('hud--paused')
    els.paw.classList.toggle('hud__paw--nudge', show)
  }
  setInterval(updateNudge, 1000)
  on(Events.Click, () => { lastPawAt = Date.now(); els.paw.classList.remove('hud__paw--nudge') })
  if (import.meta.env?.DEV) window.__pawNudge = () => { lastPawAt = 0; updateNudge() } // dev: force the nudge

  els.paw.addEventListener('click', (e) => {
    gsap
      .timeline()
      .to(e.currentTarget, { scale: 0.92, duration: 0.06, ease: 'power1.out' })
      .to(e.currentTarget, { scale: 1, duration: 0.18, ease: 'back.out(2.5)' })
    emit('paw:click') // routed to seasonManager in main.js
  })

  // Play / pause toggle (stickiness): freezes the game so the Emperor's idle
  // penalty + tribute can't tick while the player is away. Shows ⏸ when running
  // (tap to pause) and ▶ when paused (tap to resume).
  const pauseBtn = root.querySelector('[data-pause]')
  const pauseBadge = root.querySelector('[data-pause-badge]')
  const paintPause = () => {
    const p = idle.isPaused()
    pauseBtn.textContent = p ? '▶' : '⏸'
    pauseBtn.classList.toggle('paused', p)
    pauseBtn.setAttribute('aria-label', p ? 'Resume game' : 'Pause game')
    pauseBtn.title = p
      ? 'Paused — tap to resume play'
      : "Pause / resume — freezes the game so the Emperor can't fine you while you're away"
    if (pauseBadge) pauseBadge.hidden = !p
    root.classList.toggle('hud--paused', p)
  }
  pauseBtn.addEventListener('click', () => idle.togglePause())
  on(Events.PauseChange, paintPause)
  paintPause()

  pushLog('A small kingdom begins. The fields wait.')

  // Hack-to-home button — emits an event so heroController teleports the cat.
  // Position is fixed in CSS (bottom-left corner); no JS coupling needed.
  const hackBtn = root.querySelector('[data-hack-home]')
  if (hackBtn) {
    hackBtn.addEventListener('click', () => emit(Events.HackHome))
  }

  return els
}

// Calm footer feed — update the latest message with a gentle fade-in. No
// center-screen disruption; routine events just scroll through the footer bar.
function pushLog(msg) {
  const log = document.querySelector('[data-log]')
  if (!log) return
  log.classList.add('flash')
  setTimeout(() => { log.textContent = msg; log.classList.remove('flash') }, 120)
}

// "+28m" rises off the paw, then flies into the fortune tally, which pops as it lands — the coin visibly goes where it goes.
function spawnFloatingYen(button, delta, tally = null, kind = '') {
  const rect = button.getBoundingClientRect()
  // Anchor at the paw's CENTER and offset back by half its own size so the number is centered over the clicker.
  floatFrom(rect.left + rect.width / 2, rect.top + rect.height / 2, delta, tally, kind)
}
export function floatFrom(x0, y0, delta, tally = null, kind = '') { // (exported for the Testing Field's stage)
  if (document.querySelectorAll('.float-yen').length >= LoopFeedback.floatCap) return // (a fast streak would otherwise flood the page)
  const node = document.createElement('div')
  node.className = `float-yen${kind ? ` float-yen--${kind}` : ''}`
  node.textContent = formatCoinsDelta(delta)
  node.style.left = `${x0}px`
  node.style.top = `${y0}px`
  document.body.appendChild(node)
  gsap.set(node, { xPercent: -50, yPercent: -50 })
  const tr = tally?.getBoundingClientRect()
  const tl = gsap.timeline({ onComplete: () => node.remove() })
  tl.to(node, { y: -46, duration: 0.22, ease: 'power2.out' })
  if (tr && tr.width > 0) {
    tl.to(node, { x: tr.left + tr.width / 2 - x0, y: tr.top + tr.height / 2 - y0, scale: 0.45, opacity: 0.25, duration: 0.5, ease: 'power2.in' })
    tl.add(() => { gsap.fromTo(tally, { scale: 1.05 }, { scale: 1, duration: 0.22, ease: 'power2.out', overwrite: 'auto' }) })
  } else tl.to(node, { y: -80, opacity: 0, duration: 0.5, ease: 'power1.out' })
}

function template() {
  return `
    <div class="hud__title">
      <h1>MAO KINGDOM</h1>
      <div class="subtitle">猫王国</div>
    </div>
    <div class="hud__kingdom-tabs-slot" data-kingdom-tabs></div>
    <div class="hud__wealth">
      <div class="label">FORTUNE ALL THE WAY</div>
      <div class="amount" data-wealth>0m</div>
      <div class="sublabel">MAO COIN MARKET VALUE · <span data-wealth-usd>≈ $0.00</span></div>
    </div>
    <div class="hud__streak" data-streak hidden title="Daily login streak">
      <span class="hud__streak-icon">🔥</span>
      <span class="hud__streak-num" data-streak-num>0</span>
      <span class="hud__streak-label">day streak</span>
    </div>
    <div class="hud__holiday" data-holiday hidden role="status" aria-live="polite">
      <span class="hud__holiday-emoji" data-holiday-emoji></span>
      <span class="hud__holiday-label" data-holiday-label></span>
      <span class="hud__holiday-mult" data-holiday-mult></span>
    </div>
    <div class="hud__adv-status" data-adv-status hidden role="status" aria-live="polite">
      <span class="hud__adv-emoji" data-adv-emoji>🐾</span>
      <span class="hud__adv-text"  data-adv-text>—</span>
      <span class="hud__adv-mode"  data-adv-mode>—</span>
      <button class="hud__adv-return" data-adv-return title="Bring your cat home (keep 70% of coins found so far)">⌂ Return Home</button>
    </div>
    <div class="hud__stats" data-stats>
      <button class="cashout" disabled title="Phase 3">CASH OUT</button>
      <div class="clicks" data-clicks>0</div>
      <div class="clicks-label">CLICKER COUNT</div>
      <div class="rates">
        <div class="rate"><span class="lbl">YOU</span> <span data-you-cps>0.0</span></div>
        <div class="rate"><span class="lbl">GOONS</span> <span data-goons-cps>0.0</span></div>
        <div class="rate bonus"><span class="lbl">BONUS</span> <span data-bonus>—</span></div>
      </div>
    </div>
    <div class="hud__rail">
      <button data-rail-build>BUILD</button>
      <button data-rail-pop>CITIZENS</button>
      <button data-rail-career>CAREER</button>
      <button data-rail-adventure>ADVENTURE</button>
    </div>
    <div class="hud__build" data-build-panel></div>
    <div class="hud__build hud__feed" data-feed-panel></div>
    <div class="hud__build hud__tax" data-tax-panel></div>
    <div class="hud__build hud__pop" data-pop-panel></div>
    <div class="hud__build hud__career" data-career-panel></div>
    <div class="hud__build hud__registry" data-registry-panel></div>
    <div class="hud__build hud__adventure" data-adventure-panel></div>
    <div class="hud__build hud__cloud" data-cloud-panel></div>
    <div class="hud__build hud__settings" data-settings-panel></div>
    <div class="hud__placement-banner" data-placement-banner></div>
    <div class="hud__kview" data-kview-panel></div>
    <div class="hud__plot-buy" data-plot-purchase></div>
    <div class="hud__onboard" data-onboard-panel></div>
    <button class="hud__hack-home" data-hack-home title="Press H to return home (12 tile home)">⌂ HOME</button>
    <button class="hud__workmode" data-workmode title="Toggle your citizens between farming the fields and guarding the village">
      <span class="hud__workmode-icon" data-wm-icon>⚒</span>
      <span class="hud__workmode-text">
        <span class="hud__workmode-title" data-wm-title>WORKERS ARE IN THE FIELDS</span>
        <span class="hud__workmode-action" data-wm-action>Click to recall them to defend the village</span>
        <span class="hud__workmode-sub" data-wm-sub>Max rice income · village undefended</span>
      </span>
    </button>
    <div class="hud__paw-wrap">
      <div class="hud__paw-yield" data-paw-yield>NO YIELD</div>
      <button class="hud__paw" data-paw aria-label="Tend the field">
        <svg viewBox="0 0 64 64" aria-hidden="true">
          <line x1="32" y1="8" x2="32" y2="56"/>
          <line x1="8" y1="32" x2="56" y2="32"/>
        </svg>
      </button>
      <div class="hud__combo" data-combo title="Keep a steady rhythm — clicks under a second apart build the combo; a pause drains it"><span class="hud__combo-bar" data-combo-bar></span><span class="hud__combo-label" data-combo-label>×1.0</span></div>
      <div class="hud__pause-row">
        <span class="hud__pause-badge" data-pause-badge hidden>⏸ PAUSED, Tap ▶ to Play</span>
        <button class="hud__pause" data-pause aria-label="Pause game" title="Pause / resume — freezes the game so the Emperor can't fine you while you're away">⏸</button>
      </div>
      <div class="hud__season">
        <div class="name" data-season-name>—</div>
        <div class="progress"><span data-season-bar></span></div>
        <div class="count" data-season-count>0 / 0 CLICKS</div>
      </div>
      <div class="hud__goons" data-goons></div>
    </div>
    <div class="hud__log">
      <span class="msg" data-log>—</span>
    </div>
  `
}

export { pushLog }
